import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiTrash2, FiAlertTriangle } from 'react-icons/fi';
import styles from './UserSubscriptionInfo.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * UserSubscriptionInfo - View detailed subscription information for all users
 * Shows package limits, product counts, billing status, etc.
 */
function UserSubscriptionInfo() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [packageFilter, setPackageFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [packageStats, setPackageStats] = useState({});

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Debounce search
    const [searchDebounce, setSearchDebounce] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchSubscriptionInfo();
    }, [currentPage, itemsPerPage, searchDebounce, packageFilter, statusFilter]);

    const fetchSubscriptionInfo = async () => {
        try {
            setLoading(true);
            setError(''); // Clear previous errors
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const params = new URLSearchParams({
                page: currentPage,
                limit: itemsPerPage,
                search: searchDebounce,
                packageFilter: packageFilter,
                statusFilter: statusFilter
            });

            console.log('Fetching with filters:', { packageFilter, statusFilter, page: currentPage, limit: itemsPerPage });

            const response = await axios.get(
                `${BASEURL}superadmin/user-subscription-info/${userId}?${params}`,
                { headers: { token } }
            );

            console.log('API Response:', response.data);

            if (response.data.success) {
                setUsers(response.data.data.users);
                setTotalPages(response.data.data.pagination.pages);
                setTotal(response.data.data.pagination.total);
                setPackageStats(response.data.data.packageStats || {});
                console.log(`Loaded ${response.data.data.users.length} users, total: ${response.data.data.pagination.total}`);
            } else {
                setError(response.data.error || 'Failed to load subscription info');
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching subscription info:', err);
            setError('Failed to load subscription info');
            setLoading(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'active':
                return styles.statusActive;
            case 'overdue':
                return styles.statusOverdue;
            case 'due_soon':
                return styles.statusDueSoon;
            default:
                return styles.statusInactive;
        }
    };

    const getLimitBadgeClass = (status) => {
        switch (status) {
            case 'unlimited':
                return styles.limitUnlimited;
            case 'within_limit':
                return styles.limitOk;
            case 'near_limit':
                return styles.limitWarning;
            case 'limit_reached':
                return styles.limitReached;
            default:
                return styles.limitOk;
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const handleDeleteUser = async (userId, username, mobile) => {
        const confirmMessage = `⚠️ DANGEROUS: DELETE USER\n\n` +
            `User: ${username} (${mobile})\n\n` +
            `This will permanently delete:\n` +
            `• User account\n` +
            `• All domains/stores\n` +
            `• All products\n` +
            `• All orders & customers\n` +
            `• All categories\n` +
            `• DNS records & SSL configs\n\n` +
            `⚠️ THIS CANNOT BE UNDONE!\n\n` +
            `Type "${username}" to confirm deletion:`;

        const confirmation = prompt(confirmMessage);

        if (confirmation !== username) {
            alert('❌ Deletion cancelled - username did not match');
            return;
        }

        // Double confirmation
        if (!window.confirm(`Final confirmation: Delete ${username} and ALL data?`)) {
            alert('❌ Deletion cancelled');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const adminId = localStorage.getItem('id');

            const response = await axios.post(
                `${BASEURL}admin-user-delete/${adminId}`,
                { user: userId },
                { headers: { token } }
            );

            if (response.data && response.status === 200) {
                alert(`✅ User deleted successfully!\n\n${response.data}`);
                fetchSubscriptionInfo(); // Refresh list
            } else {
                alert(`❌ Failed to delete user: ${response.data || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            alert(`❌ Failed to delete user: ${err.response?.data || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1>User Subscription Information</h1>
                    <p className={styles.subtitle}>View detailed package and billing information for all users</p>
                </div>

                {/* Package Stats Summary */}
                {Object.keys(packageStats).length > 0 && (
                    <div className={styles.statsContainer}>
                        <h3>Package Distribution</h3>
                        <div className={styles.statsGrid}>
                            {Object.entries(packageStats).map(([pkg, count]) => (
                                <div key={pkg} className={styles.statCard}>
                                    <div className={styles.statLabel}>{pkg}</div>
                                    <div className={styles.statValue}>{count}</div>
                                </div>
                            ))}
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>Total Users</div>
                                <div className={styles.statValue}>{total}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className={styles.filters}>
                    <input
                        type="text"
                        placeholder="Search by username, mobile, or domain..."
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <select
                        className={styles.filterSelect}
                        value={packageFilter}
                        onChange={(e) => {
                            setPackageFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Packages</option>
                        <option value="startup">Startup</option>
                        <option value="smallBusiness">Small Business</option>
                        <option value="business">Business</option>
                        <option value="owner-unlimited">Owner Unlimited</option>
                    </select>

                    <select
                        className={styles.filterSelect}
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Statuses</option>
                        <option value="trial">Trial</option>
                        <option value="active">Active</option>
                        <option value="pending_payment">Pending Payment</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                    </select>

                    <select
                        className={styles.filterSelect}
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                    </select>
                </div>

                {/* Loading/Error States */}
                {loading && <div className={styles.loading}>Loading subscription information...</div>}
                {error && <div className={styles.error}>{error}</div>}

                {/* User List */}
                {!loading && !error && users.length === 0 && (
                    <div className={styles.noResults}>No users found</div>
                )}

                {!loading && !error && users.length > 0 && (
                    <>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Domains</th>
                                        <th>Package</th>
                                        <th>Status</th>
                                        <th>Products</th>
                                        <th>Limit</th>
                                        <th>Billing</th>
                                        <th>Next Billing</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.userId}>
                                            <td>
                                                <div className={styles.userInfo}>
                                                    <div className={styles.userName}>{user.username}</div>
                                                    <div className={styles.userMobile}>{user.mobile}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.domainList}>
                                                    {user.domains && user.domains.length > 0 ? (
                                                        user.domains.map((domain, idx) => (
                                                            <div key={idx} className={styles.domainItem}>
                                                                <a
                                                                    href={`https://${domain.domain}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={styles.domainLink}
                                                                    title={domain.name}
                                                                >
                                                                    {domain.domain}
                                                                </a>
                                                                {domain.isSuspended && (
                                                                    <span className={styles.suspendedBadge}><FiAlertTriangle style={{ verticalAlign: 'middle' }} /> Suspended</span>
                                                                )}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className={styles.noDomain}>No domain</span>
                                                    )}
                                                    {user.domainCount > 5 && (
                                                        <div className={styles.moreDomains}>
                                                            +{user.domainCount - 5} more
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.packageInfo}>
                                                    <div className={styles.packageName}>{user.packageName}</div>
                                                    <div className={styles.packageType}>{user.packageType}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${getStatusBadgeClass(user.billingStatus)}`}>
                                                    {user.subscriptionStatus}
                                                </span>
                                            </td>
                                            <td className={styles.productsCell}>
                                                <div className={styles.productCount}>{user.currentProducts}</div>
                                            </td>
                                            <td>
                                                <span className={`${styles.limitBadge} ${getLimitBadgeClass(user.productLimitStatus)}`}>
                                                    {user.hasUnlimited ? (
                                                        '∞ Unlimited'
                                                    ) : user.productLimit === -1 ? (
                                                        '∞ Unlimited'
                                                    ) : (
                                                        `${user.productLimit} max`
                                                    )}
                                                </span>
                                                {!user.hasUnlimited && user.productLimit !== -1 && (
                                                    <div className={styles.remainingProducts}>
                                                        {user.remainingProducts} remaining
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className={styles.billingCycle}>{user.billingCycle}</div>
                                            </td>
                                            <td>
                                                <div className={styles.nextBilling}>
                                                    {formatDate(user.nextBilling)}
                                                </div>
                                                {user.daysUntilBilling !== null && (
                                                    <div className={`${styles.daysRemaining} ${getStatusBadgeClass(user.billingStatus)}`}>
                                                        {user.daysUntilBilling < 0 ? (
                                                            `${Math.abs(user.daysUntilBilling)} days overdue`
                                                        ) : user.daysUntilBilling > 365 * 10 ? (
                                                            'Lifetime'
                                                        ) : (
                                                            `${user.daysUntilBilling} days`
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className={styles.deleteButton}
                                                    onClick={() => handleDeleteUser(user.userId, user.username, user.mobile)}
                                                    disabled={loading}
                                                    title="Delete user and all related data"
                                                >
                                                    <FiTrash2 style={{ verticalAlign: 'middle' }} /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className={styles.pagination}>
                            <div className={styles.paginationInfo}>
                                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} users
                            </div>
                            <div className={styles.paginationControls}>
                                <button
                                    className={styles.pageButton}
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    First
                                </button>
                                <button
                                    className={styles.pageButton}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className={styles.pageInfo}>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    className={styles.pageButton}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                                <button
                                    className={styles.pageButton}
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

export default UserSubscriptionInfo;
